//React
import { useState } from 'react';

//Mantine
import { Button, Flex, Tooltip } from '@mantine/core';
import {
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableInstance,
  MRT_TableOptions,
} from 'mantine-react-table';

//Refine
import { useNotification, useTranslation } from '@refinedev/core';

// Custom Components
import { BillingDataGridWrapper } from 'components/common/BillingDataGridWrapper';
import { EOBToolBar } from './components/EOBToolBar';

// Interfaces
import { EOB } from 'interfaces';

// Columns
import { EOBColumns } from 'columns/Eob';

// Resource
import {
  bulkIngestEOBs,
  useCreateEob,
  useListEobs,
  useUpdateEob,
} from 'api/resource/eob';
import { EditRowButton } from 'components/common/EditRowButton';
import { uploadFileToS3 } from 'utils/fileUpload';

import { IconRefresh } from '@tabler/icons-react';
import { provincesEnum } from '@billing/validation';
import {
  CreateMutationDataParams,
  UpdateMutationDataParams,
  createMutationData,
  updateMutationData,
} from 'api/utils/MutationDataBuilder';
import { TableAlertBanner } from 'components/common/TableAlertBanner';
import { useCurrentSiteStore } from 'store/SiteStore';

export const EOBManagement = () => {
  const title = 'EOB';
  const { open } = useNotification();
  const { translate } = useTranslation();
  const [eobFile, setEobFile] = useState<File | null>(null);
  const [reviewEOBs, setReviewEOBs] = useState<boolean>(false);
  const { province } = useCurrentSiteStore();

  const createEob = useCreateEob();
  const updateClaim = useUpdateEob();

  const notifyInvalidSiteProvince = () => {
    open?.({
      key: 'error-eob-province',
      message: translate(
        'pages:eobs.messages.invalidSiteProvince',
        'Please select a site with a valid province.'
      ),
      type: 'error',
    });
  };

  const handleCreateEOB = ({
    table,
    values,
  }: CreateMutationDataParams<EOB>) => {
    const parsedProvince = provincesEnum.safeParse(values.province ?? province);
    if (!parsedProvince.success) {
      notifyInvalidSiteProvince();
      return;
    }

    return createMutationData<EOB>({
      table,
      values: {
        ...values,
        province: parsedProvince.data,
      },
      mutation: createEob,
    });
  };

  const handleUpdateEOB = ({
    table,
    values,
    row,
  }: UpdateMutationDataParams<EOB>) =>
    updateMutationData<EOB>({
      table,
      values,
      row,
      mutation: updateClaim,
    });

  const handleSubmitFile = async () => {
    if (!eobFile) {
      open?.({
        key: 'error-upload-file',
        message: 'Please select a file to upload',
        type: 'error',
      });
    } else {
      const parsedProvince = provincesEnum.safeParse(province);
      if (!parsedProvince.success) {
        notifyInvalidSiteProvince();
        return;
      }
      await uploadFileToS3([eobFile], 'qbilling-eob-files');
      await bulkIngestEOBs(eobFile, parsedProvince.data).then(() => {
        setEobFile(null);
        open?.({
          key: 'success-upload-file',
          message: 'File uploaded successfully',
          type: 'success',
        });
      });
    }
  };

  const {
    data: eobs,
    isLoading,
    isError,
    isFetching,
    refetch: refetchEOBs,
    error: eobError,
  } = useListEobs({});

  const data = reviewEOBs ? eobs.filter((eob) => eob.reviewRequired) : eobs;

  const columns: MRT_ColumnDef<EOB>[] = EOBColumns(province);

  const tableOptions: MRT_TableOptions<EOB> = {
    data: data || [],
    columns,
    state: {
      showAlertBanner: isError,
      showProgressBars: isFetching,
      isLoading,
    },
    createDisplayMode: 'modal',
    editDisplayMode: 'modal',
    enableFullScreenToggle: false,
    enableEditing: true,
    onEditingRowSave: handleUpdateEOB,
    onCreatingRowSave: handleCreateEOB,
    mantineTableBodyRowProps: ({ row }) => ({
      style: {
        color: row.original.reviewRequired ? 'red' : 'inherit',
      },
    }),
    mantineToolbarAlertBannerProps() {
      return TableAlertBanner({ isError, message: eobError?.message });
    },
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <Flex align={'center'} gap={'sm'} w={'auto'}>
          <Tooltip
            label="Loading..."
            position="top"
            transitionProps={{ duration: 500, transition: 'fade-up' }}
            disabled={!isLoading}
          >
            <Button
              onClick={() => refetchEOBs()}
              disabled={isLoading}
              variant="outline"
              data-variant="refresh"
            >
              <IconRefresh size={20} />
            </Button>
          </Tooltip>
          <EOBToolBar
            table={table}
            handleSubmitFile={handleSubmitFile}
            eobFile={eobFile}
            setEobFile={setEobFile}
          />
          <Tooltip
            label="Loading..."
            position="top"
            transitionProps={{ duration: 500, transition: 'fade-up' }}
            disabled={!isLoading}
          >
            <Button
              color="blue"
              size="sm"
              onClick={() => setReviewEOBs(!reviewEOBs)}
              disabled={isLoading || eobs.length === 0}
            >
              Filter by Review Required
            </Button>
          </Tooltip>
        </Flex>
      );
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 10,
        Cell: ({
          table,
          row,
        }: {
          table: MRT_TableInstance<EOB>;
          row: MRT_Row<EOB>;
        }) => {
          return <EditRowButton table={table} row={row} type="eob" />;
        },
      },
    },
  };

  return (
    <BillingDataGridWrapper
      title={title}
      dataTestID="eobManagement"
      tableOptions={tableOptions}
    />
  );
};
